'use client';

import { useCallback } from 'react';
import * as Blockly from 'blockly/core';
import { Camera, CircleMinus, CirclePlus, Maximize } from 'lucide-react';
import IconAdaptive from '@/components/IconAdaptive';

interface Props {
    workspaceRef: React.RefObject<Blockly.WorkspaceSvg | null>;
    isDark: boolean;
    blockZoomScale: number;
}

const SCREENSHOT_FONTS: { family: string; weight: string; src: string }[] = [
    { family: 'Cantarell', weight: '700', src: '/fonts/Cantarell-Bold.woff2' },
    { family: 'Cantarell', weight: '400', src: '/fonts/Cantarell-Medium.woff2' },
    { family: 'Corporate Logo', weight: '700', src: '/fonts/Corporate-Logo-Bold-ver3.woff2' },
    { family: 'Corporate Logo', weight: '500', src: '/fonts/Corporate-Logo-Medium-ver3.woff2' },
    { family: 'Corporate Logo', weight: '400', src: '/fonts/Corporate-Logo-Medium-ver3.woff2' },
];

async function toBase64(url: string): Promise<string> {
    const res = await fetch(url);
    if (!res.ok) {
        throw new Error(`Failed to fetch font: ${url} (${res.status})`);
    }
    const buf = await res.arrayBuffer();
    const bytes = new Uint8Array(buf);
    let bin = '';
    for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
    return btoa(bin);
}

async function buildFontFaceCSS(): Promise<string> {
    const parts = await Promise.all(
        SCREENSHOT_FONTS.map(async ({ family, weight, src }) => {
            const b64 = await toBase64(src);
            return `@font-face { font-family: '${family}'; font-weight: ${weight}; src: url('data:font/woff2;base64,${b64}') format('woff2'); }`;
        }),
    );
    return parts.join('\n');
}

let fontFaceCssCache: Promise<string> | null = null;

function getCachedFontFaceCSS(): Promise<string> {
    if (!fontFaceCssCache) {
        fontFaceCssCache = buildFontFaceCSS().catch((e) => {
            fontFaceCssCache = null;
            throw e;
        });
    }
    return fontFaceCssCache;
}

async function resolveImages(svgEl: SVGSVGElement): Promise<void> {
    const images = svgEl.querySelectorAll<SVGImageElement>('image');
    await Promise.all(
        Array.from(images).map(async (img) => {
            const href = img.getAttribute('href') ?? img.getAttributeNS('http://www.w3.org/1999/xlink', 'href') ?? '';
            if (!href || href.startsWith('data:')) return;
            try {
                const res = await fetch(href);
                const buf = await res.arrayBuffer();
                const mime = res.headers.get('content-type') ?? 'image/svg+xml';
                const bytes = new Uint8Array(buf);
                let bin = '';
                for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
                const b64 = `data:${mime};base64,${btoa(bin)}`;
                img.setAttribute('href', b64);
                img.removeAttributeNS('http://www.w3.org/1999/xlink', 'href');
                img.removeAttribute('xlink:href');
            } catch (e) {
                console.error('resolveImages failed:', href, e);
            }
        }),
    );
}

function replaceForeignObjects(svgEl: SVGSVGElement): void {
    svgEl.querySelectorAll('foreignObject').forEach((fo) => {
        const width = parseFloat(fo.getAttribute('width') ?? '0');
        const height = parseFloat(fo.getAttribute('height') ?? '0');
        const transform = fo.getAttribute('transform') ?? '';

        const g = document.createElementNS('http://www.w3.org/2000/svg', 'g');
        if (transform) g.setAttribute('transform', transform);

        const divs = fo.querySelectorAll<HTMLElement>('div[style*="color"]');
        const items: { text: string; bg: string; active: boolean }[] = [];
        divs.forEach((div) => {
            const text = div.textContent?.trim() ?? '';
            if (!text) return;
            const bg = div.style.background ?? '';
            const alphaMatch = bg.match(/rgba?\([^)]*?,\s*([0-9.]+)\s*\)/);
            const alpha = alphaMatch ? Number(alphaMatch[1]) : 0;
            const active = Math.abs(alpha - 0.3) < 0.01;
            items.push({ text, bg, active });
        });

        if (items.length === 0) {
            const outerDiv = fo.querySelector<HTMLElement>('div');
            const text = outerDiv?.textContent?.trim() ?? '';
            if (text) {
                const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
                rect.setAttribute('x', '0');
                rect.setAttribute('y', '0');
                rect.setAttribute('width', String(width));
                rect.setAttribute('height', String(height));
                rect.setAttribute('rx', '6');
                rect.setAttribute('ry', '6');
                rect.setAttribute('fill', 'rgba(255,255,255,0.3)');
                rect.setAttribute('stroke', 'rgba(255,255,255,0.6)');
                rect.setAttribute('stroke-width', '1.5');
                g.appendChild(rect);

                const t = document.createElementNS('http://www.w3.org/2000/svg', 'text');
                t.setAttribute('x', String(width / 2));
                t.setAttribute('y', String(height / 2));
                t.setAttribute('dominant-baseline', 'central');
                t.setAttribute('text-anchor', 'middle');
                t.setAttribute('font-family', 'Corporate Logo, sans-serif');
                t.setAttribute('font-size', '16px');
                t.setAttribute('font-weight', '500');
                t.setAttribute('fill', '#ffffff');
                t.textContent = text;
                g.appendChild(t);
            }
        } else {
            const itemWidth = width / items.length;
            const outerRect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
            outerRect.setAttribute('x', '0');
            outerRect.setAttribute('y', '0');
            outerRect.setAttribute('width', String(width));
            outerRect.setAttribute('height', String(height));
            outerRect.setAttribute('rx', '6');
            outerRect.setAttribute('ry', '6');
            outerRect.setAttribute('fill', 'none');
            outerRect.setAttribute('stroke', 'rgba(255,255,255,0.6)');
            outerRect.setAttribute('stroke-width', '1.5');
            g.appendChild(outerRect);

            items.forEach((item, i) => {
                const x = i * itemWidth;
                if (item.active) {
                    const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
                    rect.setAttribute('x', String(x));
                    rect.setAttribute('y', '0');
                    rect.setAttribute('width', String(itemWidth));
                    rect.setAttribute('height', String(height));
                    rect.setAttribute('fill', 'rgba(255,255,255,0.3)');
                    g.appendChild(rect);
                }
                const t = document.createElementNS('http://www.w3.org/2000/svg', 'text');
                t.setAttribute('x', String(x + itemWidth / 2));
                t.setAttribute('y', String(height / 2));
                t.setAttribute('dominant-baseline', 'central');
                t.setAttribute('text-anchor', 'middle');
                t.setAttribute('font-family', 'Corporate Logo, sans-serif');
                t.setAttribute('font-size', '16px');
                t.setAttribute('font-weight', '500');
                t.setAttribute('fill', '#ffffff');
                t.textContent = item.text;
                g.appendChild(t);
            });
        }

        fo.parentElement?.replaceChild(g, fo);
    });
}

function getBlocksBBox(ws: Blockly.WorkspaceSvg): { left: number; top: number; right: number; bottom: number } | null {
    const raw = (ws as any).getBlocksBoundingBox?.();
    if (!raw) return null;
    if (typeof raw.getLeft === 'function') {
        return { left: raw.getLeft(), top: raw.getTop(), right: raw.getRight(), bottom: raw.getBottom() };
    }
    return raw as { left: number; top: number; right: number; bottom: number };
}

export default function BoardBlockEditorControls({ workspaceRef, isDark, blockZoomScale }: Props) {
    const iconBtn = isDark
        ? 'p-1 rounded transition-colors text-gray-300 hover:bg-gray-700 hover:text-white'
        : 'p-1 rounded transition-colors text-gray-500 hover:bg-gray-200 hover:text-gray-700';

    const handleZoom = (detail: 'in' | 'out' | 'reset') => {
        const ws = workspaceRef.current;
        if (!ws) return;
        if (detail === 'in') {
            ws.zoomCenter(1);
        } else if (detail === 'out') {
            ws.zoomCenter(-1);
        } else {
            ws.setScale(0.8);
            ws.scrollCenter();
        }
    };

    const handleScreenshot = useCallback(async () => {
        const ws = workspaceRef.current;
        if (!ws) return;

        const bbox = getBlocksBBox(ws);
        if (!bbox || bbox.right <= bbox.left || bbox.bottom <= bbox.top) return;

        const PADDING = 32;
        const scale = ws.getScale();
        const metrics = ws.getMetrics();
        const offsetX = metrics.absoluteLeft - metrics.viewLeft;
        const offsetY = metrics.absoluteTop - metrics.viewTop;

        const svgX = bbox.left * scale + offsetX - PADDING;
        const svgY = bbox.top * scale + offsetY - PADDING;
        const svgW = (bbox.right - bbox.left) * scale + PADDING * 2;
        const svgH = (bbox.bottom - bbox.top) * scale + PADDING * 2;

        const srcSvg = ws.getParentSvg();
        const clone = srcSvg.cloneNode(true) as SVGSVGElement;

        const removeSelectors = [
            '.blocklyScrollbarHorizontal',
            '.blocklyScrollbarVertical',
            '.blocklyToolboxDiv',
            '.blocklyFlyout',
            '.blocklyTrash',
            '.blocklyZoomControls',
            '[data-tooltip]',
        ];
        removeSelectors.forEach((sel) => {
            clone.querySelectorAll(sel).forEach((el) => el.remove());
        });

        clone.querySelector('.blocklyBlockCanvas')?.removeAttribute('clip-path');
        clone.querySelector('.blocklyBubbleCanvas')?.removeAttribute('clip-path');

        clone.querySelectorAll('defs pattern').forEach((el) => el.remove());

        const bgRect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
        bgRect.setAttribute('x', String(svgX));
        bgRect.setAttribute('y', String(svgY));
        bgRect.setAttribute('width', String(svgW));
        bgRect.setAttribute('height', String(svgH));
        bgRect.setAttribute('fill', '#ffffff');
        clone.insertBefore(bgRect, clone.firstChild);

        let fontCSS = '';
        try {
            fontCSS = await getCachedFontFaceCSS();
        } catch {}
        let defsEl = clone.querySelector('defs');
        if (!defsEl) {
            defsEl = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
            clone.insertBefore(defsEl, clone.firstChild);
        }
        const styleEl = document.createElementNS('http://www.w3.org/2000/svg', 'style');
        styleEl.textContent = fontCSS;
        defsEl.appendChild(styleEl);

        const disabledPatterns = Array.from(document.querySelectorAll('pattern')).filter((p) =>
            p.id.startsWith('blocklyDisabledPattern'),
        );
        let disabledPatternStyle = '';
        if (disabledPatterns.length > 0 && defsEl) {
            const pattern = disabledPatterns[0];
            const patternClone = pattern.cloneNode(true) as SVGPatternElement;
            defsEl.appendChild(patternClone);
            disabledPatternStyle = `--blocklyDisabledPattern: url(#${patternClone.id});`;
        }

        await resolveImages(clone);

        replaceForeignObjects(clone);

        if (disabledPatterns.length > 0) {
            const patternId = disabledPatterns[0].id;
            clone.querySelectorAll('.blocklyDisabledPattern > .blocklyPath').forEach((path) => {
                (path as SVGPathElement).setAttribute('fill', `url(#${patternId})`);
                (path as SVGPathElement).style.fillOpacity = '0.5';
            });
        }

        clone.querySelectorAll<SVGTextElement>('text').forEach((t) => {
            if (!t.getAttribute('font-family')) {
                const inShadow = t.closest('.blocklyShadow');
                const isNumberField = t.classList.contains('blocklyNumberFieldText');

                if (inShadow && isNumberField) {
                    t.setAttribute('font-family', 'Cantarell, sans-serif');
                    t.setAttribute('font-size', '15px');
                    t.setAttribute('font-weight', '600');
                    t.setAttribute('fill', '#575E75');
                } else if (inShadow) {
                    t.setAttribute('font-family', 'Corporate Logo, sans-serif');
                } else {
                    t.setAttribute('font-family', 'Corporate Logo, sans-serif');
                    t.setAttribute('fill', '#ffffff');
                }
            }
        });

        clone.setAttribute('viewBox', `${svgX} ${svgY} ${svgW} ${svgH}`);
        clone.setAttribute('width', String(svgW));
        clone.setAttribute('height', String(svgH));

        clone.removeAttribute('xmlns:xlink');
        clone.removeAttributeNS('http://www.w3.org/2000/xmlns/', 'xlink');
        clone.removeAttribute('style');

        if (disabledPatternStyle) {
            clone.setAttribute('style', disabledPatternStyle);
        }

        const mainBg = clone.querySelector<SVGRectElement>('.blocklyMainBackground');
        if (mainBg) {
            mainBg.removeAttribute('style');
            mainBg.setAttribute('fill', '#ffffff');
        }

        const svgStr = new XMLSerializer().serializeToString(clone);
        const svgBase64 = `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svgStr)))}`;

        const img = new Image();
        img.onload = () => {
            const canvas = document.createElement('canvas');
            const dpr = window.devicePixelRatio ?? 1;
            canvas.width = svgW * dpr;
            canvas.height = svgH * dpr;
            const ctx = canvas.getContext('2d')!;
            ctx.scale(dpr, dpr);
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, svgW, svgH);
            ctx.drawImage(img, 0, 0, svgW, svgH);

            canvas.toBlob((pngBlob) => {
                if (!pngBlob) return;
                const url = URL.createObjectURL(pngBlob);
                const a = document.createElement('a');
                a.href = url;
                const now = new Date();
                const pad = (n: number) => String(n).padStart(2, '0');
                const ts = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}${pad(
                    now.getHours(),
                )}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
                a.download = `schoomy-blocks-${ts}.png`;
                a.click();
                setTimeout(() => URL.revokeObjectURL(url), 1000);
            }, 'image/png');
        };
        img.onerror = (e) => {
            console.error('screenshot: SVG render failed', e);
        };
        img.src = svgBase64;
    }, [workspaceRef]);

    return (
        <div className="relative">
            <div
                className={`absolute bottom-2 right-4 z-20 flex items-center gap-0.5 px-1.5 py-1 rounded-lg backdrop-blur-sm shadow-sm border ${
                    isDark ? 'bg-color-ide-editor-dark-bg/80 border-gray-600' : 'bg-white/80 border-gray-200'
                }`}
            >
                <button onClick={handleScreenshot} className={iconBtn} title="スクリーンショット">
                    <IconAdaptive Icon={Camera} baseSize={15} breakpoints={{ md: 15 }} />
                </button>
                <button onClick={() => handleZoom('reset')} className={iconBtn} title="中心に戻す">
                    <IconAdaptive Icon={Maximize} baseSize={15} breakpoints={{ md: 15 }} />
                </button>
                <button onClick={() => handleZoom('in')} className={iconBtn} title="拡大">
                    <IconAdaptive Icon={CirclePlus} baseSize={15} breakpoints={{ md: 15 }} />
                </button>
                <button onClick={() => handleZoom('out')} className={iconBtn} title="縮小">
                    <IconAdaptive Icon={CircleMinus} baseSize={15} breakpoints={{ md: 15 }} />
                </button>
                <span
                    className={`font-cantarell text-xs tabular-nums min-w-[3em] text-center ${
                        isDark ? 'text-gray-300' : 'text-gray-500'
                    }`}
                >
                    {Math.round((blockZoomScale / 0.8) * 100)}&nbsp;%
                </span>
            </div>
        </div>
    );
}
